/**
 * useUnifiedMediaCardLifecycle.ts
 *
 * Shared lifecycle hooks for media card player transition and visibility behavior.
 */

import { useEffect } from "react";
import type { UnifiedMediaCardProps } from "../../../types/livelanding.types";
import {
  clearBodyTransformReset,
  pausePlayerByEncodedId,
  pausePlayerByName,
  releaseHtmlNoScroll,
} from "../../../utils/mediaUtils";

/**
 * Syncs player transition/quit events with cleanup and pause behavior.
 */
export function usePlayerLifecycle(encodedId: string | undefined) {
  useEffect(() => {
    const handleTransition = (event: Event) => {
      const detail = (event as CustomEvent).detail;
      const transitionTo = detail?.transitionTo ?? detail?.to ?? detail?.state;

      if (transitionTo === "default") {
        const eventPlayerName =
          detail?.playerName ??
          detail?.player_name ??
          detail?.name ??
          detail?.id ??
          detail?.playerId;
        if (eventPlayerName) {
          pausePlayerByName(eventPlayerName);
        } else {
          pausePlayerByEncodedId(encodedId, 0);
        }
        return;
      }

      if (transitionTo === "minimized") {
        releaseHtmlNoScroll();
      }
    };

    const handleQuit = () => {
      pausePlayerByEncodedId(encodedId, 0);
      clearBodyTransformReset();
    };

    document.addEventListener("fw:player:transition", handleTransition);
    document.addEventListener("fw:player:quit", handleQuit as EventListener);

    return () => {
      document.removeEventListener("fw:player:transition", handleTransition);
      document.removeEventListener("fw:player:quit", handleQuit as EventListener);
    };
  }, [encodedId]);
}

/**
 * Creates lightweight intersection observer for media variant visibility lifecycle.
 */
export function useMediaIntersectionObserver(
  variant: UnifiedMediaCardProps["variant"],
  videoRef: React.RefObject<HTMLVideoElement | null>
) {
  useEffect(() => {
    if (variant !== "media") return;
    if (globalThis.window === undefined || typeof IntersectionObserver === "undefined") return;

    const videoElement = videoRef.current;
    if (!videoElement) return;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          try {
            observer.unobserve(videoElement);
          } catch {
            // ignore
          }
        }
      });
    });

    try {
      observer.observe(videoElement);
    } catch {
      // ignore
    }

    return () => {
      try {
        observer.disconnect();
      } catch {
        // ignore
      }
    };
  }, [variant, videoRef]);
}
